// @ts-nocheck
/**
 * CreditReportDetailView — Salesforce-style Credit Report record detail.
 *
 * Tabs: Details | Related
 * Details sections: Information, Overview, Creditors, System Information
 * Related sections: Files, Credit Histories, Approval History, Credit Report Credit Files
 */
import React, { useState } from 'react';
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Edit3,
  FileText,
  StickyNote,
} from 'lucide-react';
import type { CreditReportRecord } from '../../types';
import { formatAuditUserDate } from '../../utils/creditBlockFormatters';

interface CreditReportDetailViewProps {
  record: CreditReportRecord;
  clientName: string;
  onBack: () => void;
}

/* ---- SF collapsible section ---- */

const SfSection: React.FC<{
  title: string;
  defaultCollapsed?: boolean;
  children: React.ReactNode;
}> = ({ title, defaultCollapsed = false, children }) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-gray-700/60 border-b border-gray-200 dark:border-gray-700 text-left hover:bg-gray-150 dark:hover:bg-gray-700/80 transition-colors"
      >
        {collapsed
          ? <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
          : <ChevronUp className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />}
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</span>
      </button>
      {!collapsed && <div className="px-4 py-4">{children}</div>}
      {!collapsed && <div className="h-1 bg-blue-500" />}
    </div>
  );
};

/* ---- Read-only field ---- */

const SfField: React.FC<{
  label: string;
  value: React.ReactNode;
  isLink?: boolean;
  showEdit?: boolean;
}> = ({ label, value, isLink = false, showEdit = true }) => {
  const empty = <span className="text-gray-400 dark:text-gray-500 italic">—</span>;
  const display = value == null || value === '' ? empty : value;
  return (
    <div>
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">{label}</div>
      <div className="group flex items-center gap-2 min-h-[1.75rem]">
        <div className={`text-sm flex-1 ${isLink ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-gray-100'}`}>
          {display}
        </div>
        {showEdit && (
          <button type="button" className="p-0.5 text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity" title="Edit">
            <Edit3 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

/* ---- Related list header (collapsed placeholder) ---- */

const RelatedListHeader: React.FC<{
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  count: number;
  showNew?: boolean;
  newLabel?: string;
}> = ({ icon, iconBg, title, count, showNew = false, newLabel = 'New' }) => (
  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
    <div className="flex items-center justify-between px-4 py-3">
      <div className="flex items-center gap-2">
        <span className={`flex items-center justify-center w-6 h-6 rounded ${iconBg} text-white`}>
          {icon}
        </span>
        <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
          {title} ({count})
        </span>
      </div>
      {showNew && (
        <button
          type="button"
          className="px-3 py-1 text-xs font-medium border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600"
        >
          {newLabel}
        </button>
      )}
    </div>
  </div>
);

/* ---- Date formatters ---- */

function fmtDate(v: unknown): string {
  if (!v) return '';
  const s = String(v);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function fmtNum(v: unknown): string {
  if (v == null) return '';
  const n = Number(v);
  return Number.isNaN(n) ? String(v) : n.toLocaleString();
}

function fmtCurrency(v: unknown): string {
  if (v == null) return '';
  const n = Number(v);
  return Number.isNaN(n) ? String(v) : `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/* ---- Main component ---- */

const CreditReportDetailView: React.FC<CreditReportDetailViewProps> = ({
  record,
  clientName,
  onBack,
}) => {
  const [activeTab, setActiveTab] = useState<'details' | 'related'>('details');
  const r = record;
  const ov = r.overview ?? {};
  const cr = r.creditors ?? {};
  const si = r.systemInfo;
  const files = si?.attachedFiles ?? [];
  const createdAudit = formatAuditUserDate(si?.createdBy, si?.createdDate);
  const lastModifiedAudit = formatAuditUserDate(
    (si as any)?.lastModifiedBy ?? si?.createdBy,
    (si as any)?.lastModifiedDate ?? si?.createdDate
  );

  const bureauLabel = r.bureau === 'tri_merge' ? 'Tri-Merge' :
    r.bureau ? r.bureau.charAt(0).toUpperCase() + r.bureau.slice(1) : '';

  return (
    <div className="space-y-4">
      {/* Back */}
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>

      {/* Header */}
      <div className="bg-orange-600 rounded-lg px-5 py-4 text-white">
        <p className="text-xs opacity-80">Credit Report</p>
        <h2 className="text-lg font-bold">{r.id}</h2>
      </div>

      {/* Tab bar */}
      <div className="flex gap-4 border-b border-gray-200 dark:border-gray-700">
        {(['details', 'related'] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`pb-2 px-1 text-sm font-semibold capitalize ${
              activeTab === tab
                ? 'text-gray-900 dark:text-gray-100 border-b-2 border-blue-600 dark:border-blue-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {tab === 'details' ? 'Details' : 'Related'}
          </button>
        ))}
      </div>

      {activeTab === 'details' ? (
        <div className="space-y-4">
          {/* Information */}
          <SfSection title="Information">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              <SfField label="Client" value={clientName} isLink showEdit={false} />
              <SfField label="Credit Report Name" value={r.id} showEdit={false} />
              <SfField label="First Name" value={(r as any).firstName ?? ''} />
              <SfField label="Bureau" value={bureauLabel} />
              <SfField label="Last Name" value={(r as any).lastName ?? ''} />
              <SfField label="Credit Score" value={r.creditScore != null ? String(r.creditScore) : ''} />
              <SfField label="Alias" value={r.alias ?? ''} />
              <SfField label="Grade" value={r.grade ?? ''} />
              <SfField label="Birth Date" value={(r as any).birthDate ?? ''} />
              <SfField label="Rating" value={r.rating ?? ''} />
              <SfField label="Current Address" value={(r as any).currentAddress ?? ''} />
              <SfField label="Description" value={r.description ?? ''} />
              <SfField label="Previous Address" value={(r as any).previousAddress ?? ''} />
              <SfField label="Credit Report Pull Date" value={r.pullDate ? fmtDate(r.pullDate) : ''} />
              <SfField label="Current Employer" value={r.currentEmployer ?? ''} />
              <SfField label="Product Code" value={r.productCode ?? ''} />
              <SfField label="Previous Employer" value={r.previousEmployer ?? ''} />
              <SfField label="Report Key" value={r.reportKey ?? ''} showEdit={false} />
            </div>
          </SfSection>

          {/* Overview */}
          <SfSection title="Overview">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              <SfField label="Minimum Monthly" value={ov.minimumMonthlyPayment != null ? fmtCurrency(ov.minimumMonthlyPayment) : ''} />
              <SfField label="Total Creditors" value={fmtNum(ov.totalCreditors)} />
              <SfField label="Real Estate" value={ov.realEstate != null ? fmtCurrency(ov.realEstate) : ''} />
              <SfField label="Good Standing" value={fmtNum(ov.goodStanding)} />
              <SfField label="Installment" value={ov.installment != null ? fmtCurrency(ov.installment) : ''} />
              <SfField label="Open Accounts" value={fmtNum(ov.openAccounts)} />
              <SfField label="Line of Credit" value={ov.lineOfCredit != null ? fmtCurrency(ov.lineOfCredit) : ''} />
              <SfField label="Closed Accounts" value={fmtNum(ov.closedAccounts)} />
              <SfField label="Total Debts" value={ov.totalDebts != null ? fmtCurrency(ov.totalDebts) : ''} />
              <SfField label="Credit Repository Included" value={(ov.creditRepositoryIncluded ?? []).join(', ') || ''} />
            </div>
          </SfSection>

          {/* Creditors */}
          <SfSection title="Creditors">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              <SfField label="Auto" value={cr.auto?.length != null ? String(cr.auto.length) : ''} />
              <SfField label="Mortgages" value={cr.mortgages?.length != null ? String(cr.mortgages.length) : ''} />
              <SfField label="Credit Cards" value={cr.creditCards?.length != null ? String(cr.creditCards.length) : ''} />
              <SfField label="Others" value={cr.others?.length != null ? String(cr.others.length) : ''} />
            </div>
          </SfSection>

          {/* System Information */}
          <SfSection title="System Information">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              <SfField label="Created By" value={
                createdAudit
              } showEdit={false} />
              <SfField label="Credit Report Status" value={si?.status ?? r.status ?? ''} />
              <SfField label="Last Modified By" value={
                lastModifiedAudit
              } showEdit={false} />
              <SfField label="Credit Report Failure" value={(r as any).failureMessage ?? (si?.status === 'failure' ? 'Failed' : '')} />
              <SfField label="New Files Attached Date/Time" value={(r as any).newFilesAttachedDateTime ? fmtDate((r as any).newFilesAttachedDateTime) : ''} />
              <SfField label="Total Files Attached" value={si?.totalFilesAttached != null ? String(si.totalFilesAttached) : ''} />
            </div>
          </SfSection>
        </div>
      ) : (
        /* Related tab */
        <div className="space-y-4">
          {/* Files */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center w-6 h-6 rounded bg-blue-500 text-white">
                  <FileText className="w-3.5 h-3.5" />
                </span>
                <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                  Files ({files.length})
                </span>
              </div>
              <button
                type="button"
                className="px-3 py-1 text-xs font-medium border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Add Files
              </button>
            </div>
            {files.length > 0 && (
              <div className="border-t border-gray-200 dark:border-gray-700">
                {files.map((fileName, idx) => (
                  <div key={idx} className="px-4 py-3 flex items-center gap-3 border-b border-gray-100 dark:border-gray-700/50 last:border-0">
                    <FileText className="w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-blue-600 dark:text-blue-400 hover:underline cursor-pointer">{fileName}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {r.pullDate ? new Date(r.pullDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                      </p>
                    </div>
                  </div>
                ))}
                {files.length > 3 && (
                  <div className="px-4 py-2 text-center border-t border-gray-200 dark:border-gray-700">
                    <span className="text-sm text-blue-600 dark:text-blue-400 hover:underline cursor-pointer">View All</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <RelatedListHeader
            icon={<StickyNote className="w-3.5 h-3.5" />}
            iconBg="bg-orange-500"
            title="Credit Histories"
            count={0}
            showNew
          />

          <RelatedListHeader
            icon={<StickyNote className="w-3.5 h-3.5" />}
            iconBg="bg-red-500"
            title="Approval History"
            count={r.approvalHistory?.length ?? 0}
          />

          <RelatedListHeader
            icon={<StickyNote className="w-3.5 h-3.5" />}
            iconBg="bg-orange-500"
            title="Credit Report Credit Files"
            count={0}
            showNew
          />
        </div>
      )}
    </div>
  );
};

export default CreditReportDetailView;
